import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, Printer, Check, X, Trash2, ClipboardCheck, Eye } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const emptyItem = { name: "", quantity: "", unit: "pcs" };

const statusStyle = {
  pending: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  approved: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  rejected: "text-rose-400 border-rose-500/40 bg-rose-500/10",
};

function GatePassSlip({ gp }) {
  return (
    <div id="gatepass-print" className="bg-white text-black rounded-lg p-8">
      <div className="flex items-start justify-between border-b-2 border-black pb-4">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight">GATE PASS</h2>
          <p className="text-sm">BHISM Infratech</p>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold font-mono-sku">{gp.pass_number}</p>
          <p className="text-xs uppercase tracking-widest mt-1">{gp.type} pass</p>
          <div className="mt-2 flex items-center gap-1 justify-end">
            {Array.from({ length: 18 }).map((_, i) => (
              <span key={i} className="inline-block" style={{ width: i % 3 ? 2 : 3, height: 28, background: "#000" }} />
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-8 gap-y-3 mt-5 text-sm">
        <div><span className="font-semibold">Date:</span> {gp.date}</div>
        <div><span className="font-semibold">Status:</span> <span className="uppercase font-bold">{gp.status}</span></div>
        <div><span className="font-semibold">Person Name:</span> {gp.person_name}</div>
        <div><span className="font-semibold">Company:</span> {gp.company || "—"}</div>
        <div><span className="font-semibold">Vehicle No:</span> {gp.vehicle_no || "—"}</div>
        <div><span className="font-semibold">Issued By:</span> {gp.created_by}</div>
        <div className="col-span-2"><span className="font-semibold">Purpose:</span> {gp.purpose || "—"}</div>
      </div>

      <table className="w-full mt-5 text-sm border border-black border-collapse">
        <thead>
          <tr className="border-b border-black bg-gray-100">
            <th className="border border-black px-3 py-2 text-left">#</th>
            <th className="border border-black px-3 py-2 text-left">Item Description</th>
            <th className="border border-black px-3 py-2 text-right">Quantity</th>
            <th className="border border-black px-3 py-2 text-left">Unit</th>
          </tr>
        </thead>
        <tbody>
          {gp.items.length === 0 ? (
            <tr><td colSpan={4} className="border border-black px-3 py-3 text-center">No items listed</td></tr>
          ) : gp.items.map((it, i) => (
            <tr key={i}>
              <td className="border border-black px-3 py-2">{i + 1}</td>
              <td className="border border-black px-3 py-2">{it.name}</td>
              <td className="border border-black px-3 py-2 text-right">{it.quantity}</td>
              <td className="border border-black px-3 py-2">{it.unit}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="grid grid-cols-2 gap-8 mt-16">
        <div className="text-center">
          <div className="border-t border-black pt-2 text-sm font-semibold">Security Guard</div>
        </div>
        <div className="text-center">
          <div className="border-t border-black pt-2 text-sm font-semibold">
            Approved By {gp.approved_by ? `(${gp.approved_by})` : ""}
          </div>
        </div>
      </div>
      <p className="text-[10px] text-center mt-6 text-gray-500">
        Generated by GateTrack · This is a system generated gate pass.
      </p>
    </div>
  );
}

export default function GatePass() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [passes, setPasses] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [viewGp, setViewGp] = useState(null);
  const [form, setForm] = useState({
    type: "outward", person_name: "", company: "", vehicle_no: "", purpose: "", date: today(),
    items: [{ ...emptyItem }],
  });

  const load = () => {
    const params = filter !== "all" ? { status: filter } : {};
    api.get("/gatepasses", { params }).then((r) => setPasses(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { load(); }, [filter]);

  const addRow = () => setForm({ ...form, items: [...form.items, { ...emptyItem }] });
  const removeRow = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const setRow = (i, key, val) => {
    const items = [...form.items];
    items[i][key] = val;
    setForm({ ...form, items });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const items = form.items
        .filter((it) => it.name.trim())
        .map((it) => ({ name: it.name, quantity: parseFloat(it.quantity || 0), unit: it.unit }));
      await api.post("/gatepasses", { ...form, items });
      toast.success("Gate pass created");
      setOpen(false);
      setForm({ type: "outward", person_name: "", company: "", vehicle_no: "", purpose: "", date: today(), items: [{ ...emptyItem }] });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.put(`/gatepasses/${id}/status`, null, { params: { status } });
      toast.success(`Gate pass ${status}`);
      load();
      if (viewGp?.id === id) setViewGp({ ...viewGp, status });
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/gatepasses/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const printSlip = () => window.print();

  return (
    <div className="space-y-6 animate-fade-up" data-testid="gatepass-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Gate Pass</h1>
          <p className="text-muted-foreground mt-1">Create, approve and print gate passes.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-gatepass-btn" className="bg-sky-500 hover:bg-sky-600 text-white gap-2">
              <Plus className="h-4 w-4" /> New Gate Pass
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Create Gate Pass</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="gatepass-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="outward">Outward (goods out)</SelectItem>
                      <SelectItem value="inward">Inward (goods in)</SelectItem>
                      <SelectItem value="visitor">Visitor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input data-testid="gatepass-date-input" type="date" required value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Person Name</Label>
                  <Input data-testid="gatepass-person-input" required value={form.person_name}
                    onChange={(e) => setForm({ ...form, person_name: e.target.value })} placeholder="Driver / Visitor name" />
                </div>
                <div className="space-y-2">
                  <Label>Company</Label>
                  <Input data-testid="gatepass-company-input" value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Optional" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vehicle No.</Label>
                  <Input data-testid="gatepass-vehicle-input" value={form.vehicle_no}
                    onChange={(e) => setForm({ ...form, vehicle_no: e.target.value })} placeholder="MH-01-AB-1234" />
                </div>
                <div className="space-y-2">
                  <Label>Purpose</Label>
                  <Input data-testid="gatepass-purpose-input" value={form.purpose}
                    onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="Reason" />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Items</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addRow} data-testid="gatepass-add-item-row"
                    className="gap-1 h-8"><Plus className="h-3 w-3" /> Row</Button>
                </div>
                {form.items.map((it, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input placeholder="Item name" value={it.name} onChange={(e) => setRow(i, "name", e.target.value)}
                      data-testid={`gatepass-item-name-${i}`} className="flex-1" />
                    <Input placeholder="Qty" type="number" step="0.01" value={it.quantity}
                      onChange={(e) => setRow(i, "quantity", e.target.value)} data-testid={`gatepass-item-qty-${i}`} className="w-24" />
                    <Input placeholder="Unit" value={it.unit} onChange={(e) => setRow(i, "unit", e.target.value)} className="w-20" />
                    {form.items.length > 1 && (
                      <Button type="button" size="icon" variant="ghost" onClick={() => removeRow(i)}
                        className="h-9 w-9 text-rose-400"><X className="h-4 w-4" /></Button>
                    )}
                  </div>
                ))}
              </div>

              <DialogFooter>
                <Button type="submit" data-testid="gatepass-submit-btn" className="bg-sky-500 hover:bg-sky-600 text-white">
                  Create Gate Pass
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-2 flex-wrap">
        {["all", "pending", "approved", "rejected"].map((s) => (
          <Button key={s} size="sm" variant={filter === s ? "default" : "outline"}
            onClick={() => setFilter(s)} data-testid={`gatepass-filter-${s}`}
            className={filter === s ? "bg-sky-500 hover:bg-sky-600 text-white capitalize" : "capitalize"}>
            {s}
          </Button>
        ))}
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Pass No.</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Person</th>
                <th className="px-4 py-3 font-medium">Vehicle</th>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="gatepass-table-body">
              {passes.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">
                  <ClipboardCheck className="h-10 w-10 mx-auto mb-2 opacity-40" /> No gate passes.
                </td></tr>
              ) : passes.map((gp) => (
                <tr key={gp.id} className="border-b border-border/50 hover:bg-white/5">
                  <td className="px-4 py-3 font-mono-sku font-semibold text-sky-400">{gp.pass_number}</td>
                  <td className="px-4 py-3 capitalize">{gp.type}</td>
                  <td className="px-4 py-3">{gp.person_name}</td>
                  <td className="px-4 py-3 font-mono-sku text-xs">{gp.vehicle_no || "—"}</td>
                  <td className="px-4 py-3 font-mono-sku text-xs">{gp.date}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={`capitalize ${statusStyle[gp.status]}`}>{gp.status}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" data-testid={`gatepass-view-${gp.id}`} onClick={() => setViewGp(gp)}
                      className="h-8 w-8 text-sky-400 hover:bg-sky-500/10"><Eye className="h-4 w-4" /></Button>
                    {isAdmin && gp.status === "pending" && (
                      <>
                        <Button size="icon" variant="ghost" data-testid={`gatepass-approve-btn-${gp.id}`} onClick={() => setStatus(gp.id, "approved")}
                          className="h-8 w-8 text-emerald-400 hover:bg-emerald-500/10"><Check className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" data-testid={`gatepass-reject-btn-${gp.id}`} onClick={() => setStatus(gp.id, "rejected")}
                          className="h-8 w-8 text-rose-400 hover:bg-rose-500/10"><X className="h-4 w-4" /></Button>
                      </>
                    )}
                    {isAdmin && (
                      <Button size="icon" variant="ghost" data-testid={`gatepass-delete-${gp.id}`} onClick={() => del(gp.id)}
                        className="h-8 w-8 text-rose-400 hover:bg-rose-500/10"><Trash2 className="h-4 w-4" /></Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* View / Print dialog */}
      <Dialog open={!!viewGp} onOpenChange={(o) => !o && setViewGp(null)}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader className="no-print">
            <DialogTitle>Gate Pass {viewGp?.pass_number}</DialogTitle>
          </DialogHeader>
          {viewGp && <GatePassSlip gp={viewGp} />}
          <DialogFooter className="no-print gap-2">
            {isAdmin && viewGp?.status === "pending" && (
              <>
                <Button onClick={() => setStatus(viewGp.id, "approved")} data-testid="gatepass-approve-btn"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2"><Check className="h-4 w-4" /> Approve</Button>
                <Button onClick={() => setStatus(viewGp.id, "rejected")} variant="outline"
                  className="gap-2 text-rose-400 border-rose-500/40"><X className="h-4 w-4" /> Reject</Button>
              </>
            )}
            <Button onClick={printSlip} data-testid="gatepass-print-btn" className="bg-sky-500 hover:bg-sky-600 text-white gap-2">
              <Printer className="h-4 w-4" /> Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
